import { sanitizeDealPromptFormatAndDevices } from '@openx/components-deal-prompt/lib/utils/filters/sanitizeDealPromptFormatAndDevices';
import type {
  DealPromptFilterPlatform,
  DealPromptFilterSegmentSearchMode,
  DealPromptFilters,
} from '@openx/components-deal-prompt/lib/utils/types';
import type { DirectDealsListFilters } from '@openx/components-direct-deals/types';
import { difference } from '@openx/components-form/difference';
import type { OrganizationsListFilters } from '@openx/components-organization/lib/types';
import type { ProvidersListFilters } from '@openx/components-provider/lib/components/types';
import type { LalSegmentsListFilters, SegmentsListFilters } from '@openx/components-segment/types';
import type { Gender } from '@openx/types/gqlTypes';
import type { InventoryListsListFilters } from '@openx/types/inventoryList';
import { type AllTableCriteria, SortDirection } from '@openx/types/tableCriteria';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import type { AudiencesListFilters } from '../modules/audiences/AudiencesList/types';
import type { CreativesListFilters } from '../modules/creatives/CreativesList/types';
import type { DealListFilters } from '../modules/deals/DealsList/types';
import type { DealsSensitiveCategoriesListFilters } from '../modules/deals-sensitive-categories/types';
import type { CuratedDealBuyerDiscountsListFilters } from '../modules/discounts/List/types';
import type { TaxonomyListFilters } from '../modules/taxonomy/types';
import { isUrlParamsEqual } from '../utils/isUrlParamsEqual';

type CriteriaSetter<Key extends ListKey> = (
  searchParams: URLSearchParams,
  initialCriteria: DefaultTableCriteria<Key>
) => void;

type TableCriteriaSetters = {
  [Key in ListKey]: CriteriaSetter<Key>;
};

type ListSearchParams = {
  creatives?: string;
  curatedDealBuyerDiscounts?: string;
  deals?: string;
  inventoryLists?: string;
  lalSegments?: string;
  directDeals?: string;
  organizations?: string;
  providers?: string;
  segments?: string;
  taxonomy?: string;
  dealPrompt?: string;
  audiences?: string;
  sensitiveCategories?: string;
};
type ListKey = keyof ListSearchParams;

type FilterMapping = {
  creatives: CreativesListFilters;
  curatedDealBuyerDiscounts: CuratedDealBuyerDiscountsListFilters;
  deals: DealListFilters;
  inventoryLists: InventoryListsListFilters;
  lalSegments: LalSegmentsListFilters;
  directDeals: DirectDealsListFilters;
  organizations: OrganizationsListFilters;
  providers: ProvidersListFilters;
  segments: SegmentsListFilters;
  taxonomy: TaxonomyListFilters;
  dealPrompt: DealPromptFilters;
  audiences: AudiencesListFilters;
  sensitiveCategories: DealsSensitiveCategoriesListFilters;
};

export type DefaultTableCriteria<Key extends ListKey> = Required<AllTableCriteria<FilterMapping[Key]>>;

const uniqueSegmentFilterKeys = [
  'reach',
  'price_cpm',
  'price_pom',
  'updated_date',
  'created_date',
  'segment_category',
  'segment_sub_category',
  'segment_type',
  'provider',
  'account',
  'regions',
  'countries',
  'sensitive_categories',
] as const;

const convertSegmentCriteriaToSearchParams = (
  diff: Partial<AllTableCriteria<SegmentsListFilters>>,
  searchParams: URLSearchParams
) => {
  uniqueSegmentFilterKeys.forEach(key => {
    const value = diff.filters?.[key];

    if (value !== undefined && value !== null) {
      searchParams.append(key, value as string);
    }
  });

  return searchParams;
};

function convertCriteriaToSearchParams<Key extends ListKey>(
  criteria: AllTableCriteria,
  defaultCriteria: DefaultTableCriteria<Key>,
  key: Key
): URLSearchParams {
  const diff = difference(criteria, defaultCriteria);
  const searchParams = new URLSearchParams();

  // main filters
  if (diff.sort?.column) {
    searchParams.append('column', diff.sort.column);
  }

  if (diff.sort?.direction) {
    searchParams.append('sortDirection', diff.sort.direction);
  }

  if (diff.filters?.phrase) {
    searchParams.append('phrase', diff.filters.phrase);
  }

  if (diff.pagination?.pageNumber) {
    searchParams.append('page', diff.pagination.pageNumber);
  }

  // handle specific keys for now only for segments
  if (key === 'segments') {
    return convertSegmentCriteriaToSearchParams(diff, searchParams);
  }

  if (diff.filters?.account) {
    searchParams.append('account', diff.filters.account);
  }

  if (diff.filters?.exchange_deal_id) {
    searchParams.append('deal_id', diff.filters.exchange_deal_id);
  }

  if (diff.filters?.status) {
    searchParams.append('status', diff.filters.status);
  }

  if (diff.filters?.ad_format) {
    searchParams.append('ad_format', diff.filters.ad_format);
  }

  if (diff.filters?.createdBy) {
    searchParams.append('createdBy', diff.filters.createdBy);
  }

  if (diff.filters?.demand_partner) {
    searchParams.append('demand_partner', diff.filters.demand_partner);
  }

  if (diff.filters?.segment_category) {
    searchParams.append('category', diff.filters.segment_category);
  }

  if (diff.filters?.segment_source) {
    searchParams.append('source', diff.filters.segment_source);
  }

  if (diff.filters?.segment_sub_category) {
    searchParams.append('subCategory', diff.filters.segment_sub_category);
  }

  if (diff.filters?.segment_type) {
    searchParams.append('type', diff.filters.segment_type);
  }

  if (diff.filters?.provider) {
    searchParams.append('provider', diff.filters.provider);
  }

  if (diff.filters?.direct_audience_provider) {
    searchParams.append('direct_audience_provider', diff.filters.direct_audience_provider);
  }

  if (diff.filters?.addressable_reach) {
    searchParams.append('addressable_reach', diff.filters.addressable_reach);
  }

  if (diff.filters?.my_audiences) {
    searchParams.append('my_audiences', diff.filters.my_audiences);
  }

  // Marketplace Filters
  const directDealsFilterParams: (keyof Omit<DirectDealsListFilters, 'phrase' | 'tab'>)[] = [
    'formats',
    'devices',
    'country',
    'ctv_type',
    'channels',
    'geo_region',
    'deal_types',
    'sensitive_categories',
    'cpm_range',
    'unique_visitors',
    'impressions',
  ];

  directDealsFilterParams.forEach(filterParam => {
    if (diff.filters?.[filterParam]) {
      searchParams.append(filterParam, diff.filters[filterParam].join(','));
    }
  });

  if (diff.filters?.updated_date) {
    searchParams.append('updated_date', diff.filters.updated_date);
  }

  if (diff.filters?.audience_distribution) {
    searchParams.append('audience_distribution', diff.filters.audience_distribution);
  }

  if (diff.filters?.tab) {
    searchParams.append('tab', diff.filters.tab);
  }

  // Deal Prompt filters
  if (diff.filters?.format) {
    searchParams.append('format', diff.filters.format.join(','));
  }

  if (diff.filters?.geography) {
    searchParams.append('geography', diff.filters.geography.join(','));
  }

  if (diff.filters?.goal) {
    searchParams.append('goal', diff.filters.goal);
  }

  if (diff.filters?.ds_impressions) {
    searchParams.append('ds_impressions', diff.filters.ds_impressions);
  }

  if (diff.filters?.ds_viewability) {
    searchParams.append('ds_viewability', diff.filters.ds_viewability);
  }

  if (diff.filters?.ds_device) {
    searchParams.append('ds_device', diff.filters.ds_device);
  }

  if (diff.filters?.ds_video_placement) {
    searchParams.append('ds_video_placement', diff.filters.ds_video_placement);
  }

  if (diff.filters?.ds_content_vertical) {
    searchParams.append('ds_content_vertical', diff.filters.ds_content_vertical);
  }

  if (diff.filters?.ds_cpm) {
    searchParams.append('ds_cpm', diff.filters.ds_cpm);
  }

  if (diff.filters?.platform) {
    searchParams.append('platform', diff.filters.platform);
  }

  if (diff.filters?.ds_is_direct) {
    searchParams.append('ds_is_direct', diff.filters.ds_is_direct);
  }

  // Deal Prompt audience filters (Audiences context)
  if (diff.filters?.state) {
    searchParams.append('state', diff.filters.state.join(','));
  }
  if (diff.filters?.dma) {
    searchParams.append('dma', diff.filters.dma.join(','));
  }
  if (diff.filters?.gender) {
    searchParams.append('gender', diff.filters.gender);
  }
  if (diff.filters?.age) {
    searchParams.append('age', diff.filters.age.join(','));
  }
  if (diff.filters?.uniqueUsersReach !== undefined && diff.filters?.uniqueUsersReach !== null) {
    searchParams.append('unique_users_reach', String(diff.filters.uniqueUsersReach));
  }
  if (diff.filters?.category) {
    searchParams.append('category', diff.filters.category.join(','));
  }
  if (diff.filters?.sub_category) {
    searchParams.append('subCategory', diff.filters.sub_category.join(','));
  }
  if (diff.filters?.segment_search_mode) {
    searchParams.append('segment_search_mode', diff.filters.segment_search_mode);
  }
  if (diff.filters?.region) {
    searchParams.append('region', diff.filters.region);
  }
  if (diff.filters?.country) {
    searchParams.append('country', diff.filters.country.join(','));
  }

  // Curated Deal Buyer Discounts filters
  if (diff.filters?.demand_partner_id) {
    searchParams.append('demand_partner_id', diff.filters.demand_partner_id.join(','));
  }

  // Inventory Lists filters
  if (diff.filters?.account_uid?.length) {
    searchParams.append('account_uid', diff.filters.account_uid.join(','));
  }

  return searchParams;
}

function setSharedCriteria<Key extends ListKey>(
  searchParams: URLSearchParams,
  initialCriteria: DefaultTableCriteria<Key>
) {
  const column = searchParams.get('column');
  const page = searchParams.get('page');
  const phrase = searchParams.get('phrase');
  const sortDirection = searchParams.get('sortDirection');

  if (phrase) {
    initialCriteria.filters.phrase = phrase;
  }

  if (page) {
    initialCriteria.pagination.pageNumber = +page;
  }

  if (column) {
    initialCriteria.sort.column = column;
  }

  if (sortDirection) {
    initialCriteria.sort.direction = sortDirection === SortDirection.DESC ? SortDirection.DESC : SortDirection.ASC;
  }
}

const tableCriteriaSetters: TableCriteriaSetters = {
  creatives: (searchParams: URLSearchParams, initialCriteria: DefaultTableCriteria<'creatives'>) => {
    const phrase = searchParams.get('phrase');
    const status = searchParams.get('status');
    const adFormat = searchParams.get('ad_format');

    if (phrase) {
      initialCriteria.filters.phrase = phrase;
    }

    if (status) {
      initialCriteria.filters.status = status;
    }

    if (adFormat) {
      initialCriteria.filters.ad_format = adFormat;
    }
  },
  audiences: (searchParams: URLSearchParams, initialCriteria: DefaultTableCriteria<'audiences'>) => {
    const phrase = searchParams.get('phrase');
    const status = searchParams.get('status');
    const reach = searchParams.get('addressable_reach');
    const partner = searchParams.get('account');
    const direct_audience_provider = searchParams.get('direct_audience_provider');
    const updatedDate = searchParams.get('updated_date');
    const createdBy = searchParams.get('createdBy');
    const myAudiences = searchParams.get('my_audiences');

    if (phrase) {
      initialCriteria.filters.phrase = phrase;
    }

    if (status) {
      initialCriteria.filters.status = status.split(',');
    }

    if (reach) {
      initialCriteria.filters.addressable_reach = reach.split(',');
    }

    if (partner) {
      initialCriteria.filters.account = partner.split(',');
    }

    if (direct_audience_provider) {
      initialCriteria.filters.direct_audience_provider = direct_audience_provider.split(',');
    }

    if (updatedDate) {
      initialCriteria.filters.updated_date = updatedDate.split(',');
    }

    if (createdBy) {
      initialCriteria.filters.createdBy = createdBy.split(',');
    }

    if (myAudiences) {
      initialCriteria.filters.my_audiences = myAudiences;
    }
  },
  curatedDealBuyerDiscounts: (
    searchParams: URLSearchParams,
    initialCriteria: DefaultTableCriteria<'curatedDealBuyerDiscounts'>
  ) => {
    const demandPartnerId = searchParams.get('demand_partner_id');

    if (demandPartnerId) {
      initialCriteria.filters.demand_partner_id = demandPartnerId.split(',');
    }
  },
  deals: (searchParams: URLSearchParams, initialCriteria: DefaultTableCriteria<'deals'>) => {
    const account = searchParams.get('account');
    const createdBy = searchParams.get('createdBy');
    const status = searchParams.get('status');
    const deal_id = searchParams.get('deal_id');
    const demand_partner = searchParams.get('demand_partner');

    if (account) {
      initialCriteria.filters.account = account;
    }

    if (deal_id) {
      initialCriteria.filters.exchange_deal_id = deal_id;
    }

    if (createdBy) {
      initialCriteria.filters.createdBy = createdBy;
    }

    if (status) {
      initialCriteria.filters.status = status;
    }

    if (demand_partner) {
      initialCriteria.filters.demand_partner = demand_partner;
    }
  },
  inventoryLists: (searchParams: URLSearchParams, initialCriteria: DefaultTableCriteria<'inventoryLists'>) => {
    const phrase = searchParams.get('phrase');
    const account_uid = searchParams.get('account_uid');

    if (phrase) {
      initialCriteria.filters.phrase = phrase;
    }

    if (account_uid) {
      initialCriteria.filters.account_uid = account_uid.split(',');
    }
  },
  lalSegments: (searchParams: URLSearchParams, initialCriteria: DefaultTableCriteria<'lalSegments'>) => {
    const account = searchParams.get('account');
    const provider = searchParams.get('provider');
    const status = searchParams.get('status');

    if (account) {
      initialCriteria.filters.account = account;
    }

    if (provider) {
      initialCriteria.filters.provider = provider;
    }

    if (status) {
      initialCriteria.filters.status = status;
    }
  },
  directDeals: (searchParams: URLSearchParams, initialCriteria: DefaultTableCriteria<'directDeals'>) => {
    const tab = searchParams.get('tab') as DirectDealsListFilters['tab'];

    if (tab) {
      initialCriteria.filters.tab = tab;
    }

    const audienceDistribution = searchParams.get('audience_distribution') as DirectDealsListFilters['tab'];

    if (audienceDistribution) {
      initialCriteria.filters.audience_distribution = audienceDistribution;
    }

    const filterParams: (keyof Omit<DirectDealsListFilters, 'phrase' | 'tab' | 'audience_distribution'>)[] = [
      'formats',
      'devices',
      'country',
      'ctv_type',
      'channels',
      'geo_region',
      'deal_types',
      'sensitive_categories',
      'cpm_range',
      'unique_visitors',
      'impressions',
    ];

    filterParams.forEach(filterParam => {
      const value = searchParams.get(filterParam);

      if (value) {
        initialCriteria.filters[filterParam] = value.split(',');
      }
    });
  },
  organizations: () => undefined,
  providers: () => undefined,
  segments: (searchParams: URLSearchParams, initialCriteria: DefaultTableCriteria<'segments'>) => {
    const account = searchParams.get('account');
    const provider = searchParams.get('provider');
    const status = searchParams.get('status');
    const reach = searchParams.get('reach');
    const updated_date = searchParams.get('updated_date');
    const created_date = searchParams.get('created_date');
    const type = searchParams.get('type');
    const price_cpm = searchParams.get('price_cpm');
    const price_pom = searchParams.get('price_pom');
    const category = searchParams.get('category');
    const subCategory = searchParams.get('subCategory');
    const regions = searchParams.get('regions');
    const countries = searchParams.get('countries');

    if (account) {
      initialCriteria.filters.account = account;
    }

    if (provider) {
      initialCriteria.filters.provider = provider;
    }

    if (status) {
      initialCriteria.filters.status = status;
    }

    if (reach) {
      initialCriteria.filters.reach = reach;
    }

    if (updated_date) {
      initialCriteria.filters.updated_date = updated_date;
    }

    if (created_date) {
      initialCriteria.filters.created_date = created_date;
    }

    if (type) {
      initialCriteria.filters.segment_type = type;
    }

    if (price_cpm) {
      initialCriteria.filters.price_cpm = price_cpm;
    }

    if (price_pom) {
      initialCriteria.filters.price_pom = price_pom;
    }

    if (category) {
      initialCriteria.filters.segment_category = category;
    }

    if (subCategory) {
      initialCriteria.filters.segment_sub_category = subCategory;
    }

    if (regions) {
      initialCriteria.filters.regions = regions;
    }

    if (countries) {
      initialCriteria.filters.countries = countries;
    }
  },
  taxonomy: (searchParams: URLSearchParams, initialCriteria: DefaultTableCriteria<'taxonomy'>) => {
    const category = searchParams.get('category');
    const source = searchParams.get('source');
    const subCategory = searchParams.get('subCategory');
    const type = searchParams.get('type');

    if (category) {
      initialCriteria.filters.segment_category = category;
    }

    if (source) {
      initialCriteria.filters.segment_source = source;
    }

    if (subCategory) {
      initialCriteria.filters.segment_sub_category = subCategory;
    }

    if (type) {
      initialCriteria.filters.segment_type = type;
    }
  },
  dealPrompt: (searchParams: URLSearchParams, initialCriteria: DefaultTableCriteria<'dealPrompt'>) => {
    const format = searchParams.get('format');
    const geography = searchParams.get('geography');
    const goal = searchParams.get('goal');
    const impressions = searchParams.get('ds_impressions');
    const viewability = searchParams.get('ds_viewability');
    const deviceType = searchParams.get('ds_device');
    const videoPlacement = searchParams.get('ds_video_placement');
    const category = searchParams.get('ds_content_vertical');
    const cpm = searchParams.get('ds_cpm');
    const platform = searchParams.get('platform');
    const isDirect = searchParams.get('ds_is_direct');
    const state = searchParams.get('state');
    const dma = searchParams.get('dma');
    const gender = searchParams.get('gender');
    const age = searchParams.get('age');
    const uniqueUsersReach = searchParams.get('unique_users_reach');
    const segmentSearchMode = searchParams.get('segment_search_mode');
    const account = searchParams.get('account');
    const audienceCategory = searchParams.get('category');
    const subCategory = searchParams.get('subCategory');
    const region = searchParams.get('region');

    if (format) {
      initialCriteria.filters.format = format.split(',') as DealPromptFilters['format'];
    }

    if (geography) {
      initialCriteria.filters.geography = geography.split(',');
    }

    if (goal) {
      initialCriteria.filters.goal = goal as DealPromptFilters['goal'];
    }

    if (impressions) {
      initialCriteria.filters.ds_impressions = impressions;
    }

    if (viewability) {
      initialCriteria.filters.ds_viewability = viewability;
    }

    if (deviceType) {
      initialCriteria.filters.ds_device = deviceType.split(',');
    }

    if (videoPlacement) {
      initialCriteria.filters.ds_video_placement = videoPlacement.split(',');
    }

    if (category) {
      initialCriteria.filters.ds_content_vertical = category.split(',');
    }

    if (cpm) {
      initialCriteria.filters.ds_cpm = cpm;
    }

    if (platform) {
      initialCriteria.filters.platform = platform.split(',') as DealPromptFilterPlatform[];
    }

    if (isDirect) {
      initialCriteria.filters.ds_is_direct = [isDirect];
    }

    if (state) {
      initialCriteria.filters.state = state.split(',');
    }

    if (dma) {
      initialCriteria.filters.dma = dma.split(',');
    }

    if (gender) {
      initialCriteria.filters.gender = gender as Gender;
    }

    if (age) {
      initialCriteria.filters.age = age.split(',');
    }

    if (uniqueUsersReach) {
      initialCriteria.filters.uniqueUsersReach = uniqueUsersReach;
    }

    if (segmentSearchMode) {
      initialCriteria.filters.segment_search_mode = segmentSearchMode as DealPromptFilterSegmentSearchMode;
    }

    if (account) {
      initialCriteria.filters.account = account.split(',');
    }

    if (audienceCategory) {
      initialCriteria.filters.category = audienceCategory.split(',');
    }

    if (subCategory) {
      initialCriteria.filters.sub_category = subCategory.split(',');
    }

    if (region) {
      initialCriteria.filters.region = region;
    }

    initialCriteria.filters = sanitizeDealPromptFormatAndDevices(initialCriteria.filters);
  },
  sensitiveCategories: (
    searchParams: URLSearchParams,
    initialCriteria: DefaultTableCriteria<'sensitiveCategories'>
  ) => {
    const category = searchParams.get('category');
    const phrase = searchParams.get('phrase');

    if (category) {
      initialCriteria.filters.category = category.split(',');
    }

    if (phrase) {
      initialCriteria.filters.phrase = phrase;
    }
  },
};

export const listSearchParams: ListSearchParams = {};

export const useListCriteria = <Key extends ListKey>(key: Key, defaultCriteria: DefaultTableCriteria<Key>) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const getInitialCriteria = useCallback(() => {
    const initialCriteria = structuredClone(defaultCriteria);

    // mutate 'initialCriteria' object
    tableCriteriaSetters[key](searchParams, initialCriteria);
    setSharedCriteria(searchParams, initialCriteria);

    return initialCriteria;
  }, [defaultCriteria, key, searchParams]);

  const updateListSearchParams = useCallback(
    (newSearchParams: string) => {
      listSearchParams[key] = newSearchParams;
    },
    [key]
  );

  const [criteria, setCriteria] = useState<AllTableCriteria<FilterMapping[Key]>>(getInitialCriteria());

  useEffect(() => {
    const criteriaSearchParams = convertCriteriaToSearchParams(criteria, defaultCriteria, key);

    if (!isUrlParamsEqual(searchParams, criteriaSearchParams)) {
      setSearchParams(criteriaSearchParams);
    }

    return () => updateListSearchParams(searchParams.toString());
  }, [setSearchParams, updateListSearchParams, criteria, defaultCriteria, searchParams, key]);

  return {
    criteria,
    setCriteria,
  };
};
